import axios from 'axios'
import api from './api'

import persistentStore from '../persistentStore'
import { auth } from '../../presentation/components/pages/auth/firebaseConfig'

/**
 * Creates an Axios instance with predefined configuration.
 *
 * Configuration includes:
 * - `baseURL`: The base URL for API requests, derived from `api.settlement.BaseUrl`.
 * - `headers`: Default headers for all requests:
 *   - `Accept`: Specifies that the client accepts JSON responses.
 *   - `Content-Type`: Indicates that the request body is in JSON format.
 *   - `app`: The application name, sourced from the `REACT_APP_APPLICATION_NAME` environment variable or defaults to 'backOffice'.
 *   - `timeZone`: The client's current time zone, determined using `Intl.DateTimeFormat`.
 *
 * @constant {AxiosInstance} request - Configured Axios instance for making HTTP requests.
 */
const request = axios.create({
  baseURL: api.settlement.BaseUrl,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    app: process.env.REACT_APP_APPLICATION_NAME || 'backOffice',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
})


request.interceptors.request.use(async function (config) {
  try {
    config.headers.authorization = persistentStore.getToken()
    return config
  } catch (error) {
    /**
     * Retrieves the current user's authentication token.
     * The token is fetched from the `auth` object and forces a refresh
     * to ensure the token is up-to-date.
     *
     * @constant
     * @type {Promise<string>}
     * @returns {Promise<string>} A promise that resolves to the user's ID token.
     */
    const token = (await auth.currentUser.getIdToken(true)) || null
    if (token) {
      persistentStore.setToken(token)
      config.headers.authorization = token
    }

    return config
  }
})

export default request
